import React, { useEffect, useState } from 'react';
import './AddSubstantialChangeTypeModal.scss';
import { FormControl, InputLabel, MenuItem, Select, TextField, FormHelperText, Switch, FormControlLabel } from '@material-ui/core';
import SaveButton from '../../../../Util/Buttons/SaveButton/SaveButton';
import { DEBOUNCE_TIME_IN_MILLISECONDS, LanguageIdOptions, SubstantialChangesType, useModalStyles } from '../SubstantialChangesConstants';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '../../../../../hooks/useDebounce';

type SubstantialChangeTypeFormProps = {
  data: any;
  operationType?: number;
  closeModal: (value: boolean) => void;
  cancel?: (value: boolean) => void;
  onSubmit: (formData: any) => void;
  disableDraftSubmit: boolean;
  submitting: boolean;
};

export const SubstantialChangeTypeForm: React.FC<SubstantialChangeTypeFormProps> = ({
  data,
  operationType,
  closeModal,
  cancel,
  onSubmit,
  disableDraftSubmit,
  submitting
}) => {
  const { t } = useTranslation();
  const classes = useModalStyles();

  const [formData, setFormData] = useState({
    id: data?.id || null,
    isActive: data?.active,
    operationType: operationType || null,
    nameEn: data?.translations?.find(t => t.languageId === LanguageIdOptions.en)?.name || '',
    nameEs: data?.translations?.find(t => t.languageId === LanguageIdOptions.es)?.name || '',
    nameFr: data?.translations?.find(t => t.languageId === LanguageIdOptions.fr)?.name || '',
    namePt: data?.translations?.find(t => t.languageId === LanguageIdOptions.pt)?.name || '',
  });

  const [errors, setErrors] = useState({
    operationType: '',
    nameEn: '',
    nameEs: '',
    nameFr: '',
    namePt: '',
  });

  const [inputValues, setInputValues] = useState({
    nameEn: formData.nameEn,
    nameEs: formData.nameEs,
    nameFr: formData.nameFr,
    namePt: formData.namePt,
  });

  const [lastEdited, setLastEdited] = useState<string | null>(null);

  const debouncedInputValues = useDebounce(inputValues, DEBOUNCE_TIME_IN_MILLISECONDS);

  const handleChange = (event: React.ChangeEvent<{ name?; value }>) => {
    const { name, value } = event.target;
    if (name === 'operationType') {
      setFormData({
        ...formData,
        operationType: value,
      });
      setErrors(prev => ({ ...prev, operationType: value ? '' : t('required_data') }));
    } else {
      setInputValues({
        ...inputValues,
        [name as string]: value,
      });
      setLastEdited(name as string);
      setErrors(prev => ({ ...prev, [name as string]: value ? '' : t('required_data') }));
    }
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      isActive: event.target.checked,
    });
  };

  useEffect(() => {
    if (!lastEdited) return;

    const allLanguagesEmptyBefore = !formData.nameEn && !formData.nameEs && !formData.nameFr && !formData.namePt;
    if (allLanguagesEmptyBefore) {
      const valueToPropagate = debouncedInputValues[lastEdited];
      setFormData(prev => ({
        ...prev,
        nameEn: valueToPropagate,
        nameEs: valueToPropagate,
        nameFr: valueToPropagate,
        namePt: valueToPropagate,
      }));
      setInputValues({
        nameEn: valueToPropagate,
        nameEs: valueToPropagate,
        nameFr: valueToPropagate,
        namePt: valueToPropagate,
      });
      setErrors(prev => ({
        ...prev,
        nameEn: valueToPropagate ? '' : t('required_data'),
        nameEs: valueToPropagate ? '' : t('required_data'),
        nameFr: valueToPropagate ? '' : t('required_data'),
        namePt: valueToPropagate ? '' : t('required_data'),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [lastEdited]: debouncedInputValues[lastEdited],
      }));
      setErrors(prev => ({
        ...prev,
        [lastEdited]: debouncedInputValues[lastEdited] ? '' : t('required_data'),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInputValues]);

  const validate = () => {
    const newErrors: typeof errors = {
      operationType: formData.operationType ? '' : t('required_data'),
      nameEn: inputValues.nameEn ? '' : t('required_data'),
      nameEs: inputValues.nameEs ? '' : t('required_data'),
      nameFr: inputValues.nameFr ? '' : t('required_data'),
      namePt: inputValues.namePt ? '' : t('required_data'),
    };
    setErrors(newErrors);
    return Object.values(newErrors).every(e => !e);
  };

  return (
    <div className={classes.paper}>
      <strong>{t('add_substantial_and_fundamental_change')}</strong>
      <section>
        <FormControl fullWidth error={!!errors.operationType}>
          <InputLabel id="operation-type-label">{t('operation_type')}</InputLabel>
          <Select
            labelId="operation-type-label"
            id="operation-type"
            name="operationType"
            value={formData.operationType as SubstantialChangesType}
            label={t('operation_type')}
            onChange={handleChange}
          >
            <MenuItem value={SubstantialChangesType.Sovereingn}>{t('sovereign_guarantee_operation')}</MenuItem>
            <MenuItem value={SubstantialChangesType.NonSovereingn}>{t('not_sovereign_guarantee_operation')}</MenuItem>
          </Select>
          {!!errors.operationType && <FormHelperText>{errors.operationType}</FormHelperText>}
          <div className='substantial-change-active'>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleSwitchChange}
                  name="isActive"
                />
              }
              label={formData.isActive ? t('enabled') : t('no_enabled')}
              labelPlacement="start"
            />
          </div>
          <br />
          <span className='translations-title'>{t('menu_cm_translations')}</span>
          <div className="translations-content">
            <TextField
              fullWidth
              id="substantial-change-es"
              name="nameEs"
              label={t('spanish')}
              value={inputValues.nameEs}
              onChange={handleChange}
              multiline
              required
              error={!!errors.nameEs}
              helperText={errors.nameEs}
            />
            <TextField
              fullWidth
              id="substantial-change-en"
              name="nameEn"
              label={t('english')}
              value={inputValues.nameEn}
              onChange={handleChange}
              multiline
              required
              error={!!errors.nameEn}
              helperText={errors.nameEn}
            />
            <TextField
              fullWidth
              id="substantial-change-fr"
              name="nameFr"
              label={t('french')}
              value={inputValues.nameFr}
              onChange={handleChange}
              multiline
              required
              error={!!errors.nameFr}
              helperText={errors.nameFr}
            />
            <TextField
              fullWidth
              id="substantial-change-pt"
              name="namePt"
              label={t('portuguese')}
              value={inputValues.namePt}
              onChange={handleChange}
              multiline
              required
              error={!!errors.namePt}
              helperText={errors.namePt}
            />
          </div>
        </FormControl>
      </section>
      <div className="modal-container-buttons">
        <button
          className={`btn btn-primary-outlined`}
          onClick={() => {
            closeModal && closeModal(false);
            cancel && cancel(false);
          }}
        >
          {t('cancel')}
        </button>

        <SaveButton
          handleClick={() => {
            if (validate()) {
              onSubmit({ ...formData, ...inputValues });
              closeModal && closeModal(false);
            }
          }}
          customText={t('save')}
          disabled={disableDraftSubmit || submitting}
        />
      </div>
    </div>
  )
};
